'use client'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { PRODUCTS } from './navData'

interface ProductsDropdownProps {
  activeHref: string
  navLinkClass: string
}

export function ProductsDropdown({ activeHref, navLinkClass }: ProductsDropdownProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const close = () => setIsOpen(false)
  const toggle = () => setIsOpen((v) => !v)

  useEffect(() => {
    if (!isOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        close()
      }
    }
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKey)
    }
  }, [isOpen])

  return (
    <div className="relative" ref={containerRef}>
      <div className="flex items-center">
        <Link
          href="/products"
          onClick={close}
          className={`${navLinkClass} pr-1`}
        >
          Продукти
        </Link>
        <button
          id="products-btn"
          type="button"
          onClick={toggle}
          aria-expanded={isOpen}
          aria-haspopup="true"
          aria-controls="products-menu"
          aria-label="Показати список продуктів"
          className="text-white/90 hover:text-white hover:bg-white/10 rounded-lg p-1.5 transition-all duration-200"
        >
          <svg
            className={`w-3.5 h-3.5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>
      {isOpen && (
        <div
          id="products-menu"
          role="menu"
          aria-labelledby="products-btn"
          className="absolute top-full left-0 mt-2 w-64 bg-deep/95 backdrop-blur-xl border border-white/15 rounded-2xl shadow-2xl shadow-deep/50 overflow-hidden z-50"
        >
          {PRODUCTS.map((product) => (
            <Link
              key={product.href}
              href={product.href}
              role="menuitem"
              onClick={close}
              className={`flex flex-col px-4 py-3.5 hover:bg-white/10 transition-colors duration-200 ${
                activeHref === product.href ? 'bg-white/10' : ''
              }`}
            >
              <span className="font-alumni text-lg font-bold text-white leading-tight">{product.label}</span>
              <span className="font-montserrat-alt text-xs text-white/60 mt-0.5">{product.description}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
